import {
  equipmentDefinition,
  sameEquipmentKey,
  type EquipmentDefinition,
  type EquipmentKey,
} from "@frontier/content/arsenal";
import type { EquipmentTier } from "@frontier/content/equipment-tiers";
import type { Profile } from "@frontier/protocol/contracts";
import type { UpgradePricing } from "./use-upgrade-prices";
import {
  EquipmentPresentation,
  equipmentDisplayParameters,
  equipmentValue,
} from "./EquipmentPresentation";
import { trainingInstallInstruction } from "./onboarding-ui";
import { installedEquipmentFocus } from "./equipment-focus";
import {
  equipmentContextLabel,
  equipmentCount,
  equipmentSelection,
  equippedTier,
  mountedCount,
} from "./equipment-selection";
import type { EquipmentContext } from "./equipment-selection";

interface Props {
  item: EquipmentDefinition;
  tier: EquipmentTier;
  context: EquipmentContext | null;
  profile: Profile;
  busy: boolean;
  progressionAllowed: boolean;
  upgradePricing: UpgradePricing;
  trainingInstall?: boolean;
  onSelect: (key: EquipmentKey | null, tier?: EquipmentTier) => Promise<void>;
  onDismantle: (key: EquipmentKey, tier: EquipmentTier) => Promise<void>;
  onUpgrade: (key: EquipmentKey, fromTier: EquipmentTier) => Promise<void>;
}

export function EquipmentItemDetail({
  item,
  tier,
  context,
  profile,
  busy,
  progressionAllowed,
  upgradePricing,
  trainingInstall = false,
  onSelect,
  onDismantle,
  onUpgrade,
}: Props) {
  const key = item.key;
  const grade = `MK ${"I".repeat(tier + 1)}`;
  const count = equipmentCount(profile.arsenal, key, tier);
  const owned = count > 0;
  const selected =
    context !== null && equippedTier(profile, context, key) === tier;
  const compatible =
    context !== null &&
    equipmentSelection(profile, context, key, tier) !== undefined;
  const otherActive =
    context?.kind === "module" && key.kind === "module" && !compatible;
  const previous = context ? installedEquipmentFocus(profile, context) : null;
  const previousDefinition = previous
    ? equipmentDefinition(previous.key)
    : null;
  const cost =
    tier === 2
      ? null
      : (upgradePricing.prices?.items.find((entry) =>
          sameEquipmentKey(entry.key, key),
        )?.steps[tier] ?? null);
  const nextTier: EquipmentTier = tier === 0 ? 1 : 2;
  const missingCredits = cost
    ? BigInt(cost.credits) - BigInt(profile.credits)
    : 0n;
  const missingComponents = cost
    ? Math.max(0, cost.components - profile.components)
    : 0;
  const upgradeShortfall = [
    missingCredits > 0n ? `${missingCredits} КР` : "",
    missingComponents > 0 ? `${missingComponents} КОМП.` : "",
  ]
    .filter(Boolean)
    .join(" и ");
  const free = count - mountedCount(profile, key, tier);
  const noFree = Boolean(profile.shipFitting) && free <= 0;
  const installDisabled = busy || !owned || selected || !compatible || noFree;
  const needsUpgradeCell = noFree && (!selected || context?.kind !== "cell");
  const upgradeDisabled =
    busy || !owned || !cost || upgradeShortfall.length > 0 || needsUpgradeCell;
  const currentParameters = previous
    ? equipmentDisplayParameters(previous.key, previous.tier, profile.hullId)
    : [];
  const newParameters = equipmentDisplayParameters(key, tier, profile.hullId);
  const comparable = newParameters.filter(after => currentParameters.some(before => before.label === after.label && before.unit === after.unit));
  const removed = currentParameters.filter(before => !newParameters.some(after => before.label === after.label && before.unit === after.unit));
  const added = newParameters.filter(after => !currentParameters.some(before => before.label === after.label && before.unit === after.unit));
  const status =
    selected && context
      ? `УСТАНОВЛЕНО · ${equipmentContextLabel(context)}`
      : owned
        ? "ТВОЁ ОСНАЩЕНИЕ"
        : "ЭТОЙ MK НА СКЛАДЕ НЕТ";
  return (
    <article
      className="fitting-detail"
      data-equipment-kind={key.kind}
      data-equipment-id={key.id}
      data-equipment-tier={tier}
      data-installed={selected}
    >
      <div className="fitting-detail-scroll">
        <EquipmentPresentation
          equipment={key}
          tier={tier}
          profile={profile}
          status={status}
        />
        {context ? (
          <section
            className="fitting-comparison"
            aria-label="Сравнение с выбранной ячейкой"
          >
            <div>
              <small>
                {selected ? "НА КОРАБЛЕ" : previous ? "ЗАМЕНИТ" : "ЗАЙМЁТ"} ·{" "}
                {equipmentContextLabel(context)}
              </small>
              <b>
                {previous && previousDefinition
                  ? `${previousDefinition.name} MK ${"I".repeat(previous.tier + 1)}`
                  : "Свободно"}
              </b>
            </div>
            {previous && !selected && (
              <>
                {(removed.length > 0 || added.length > 0) && <div className="fitting-cell-effects" aria-label="Изменение вклада ячейки">
                  {removed.length > 0 && <div><b>Убирается</b><ul>{removed.map(parameter => <li key={parameter.label}>{parameter.label}: {equipmentValue(parameter.value, parameter.unit)}</li>)}</ul></div>}
                  {added.length > 0 && <div><b>Добавляется</b><ul>{added.map(parameter => <li key={parameter.label}>{parameter.label}: {equipmentValue(parameter.value, parameter.unit)}</li>)}</ul></div>}
                  <small>Вклад выбранной ячейки; остальные предметы сохраняются.</small>
                </div>}
                {comparable.length > 0 && <table className="fitting-comparison-table">
                  <thead><tr><th scope="col">Параметр</th><th scope="col">Сейчас</th><th scope="col">После</th><th scope="col">Δ</th></tr></thead>
                  <tbody>{comparable.map(after => {
                    const before = currentParameters.find(parameter => parameter.label === after.label && parameter.unit === after.unit);
                    if (!before) return null;
                    const difference = after.value - before.value;
                    return <tr key={after.label}><th scope="row">{after.label}</th>
                      <td>{equipmentValue(before.value, before.unit)}</td><td>{equipmentValue(after.value, after.unit)}</td>
                      <td>{difference > 0 ? "+" : ""}{equipmentValue(difference, after.unit)}</td></tr>;
                  })}</tbody>
                </table>}

              </>
            )}
          </section>
        ) : (
          <p className="fitting-target-hint">
            Выбери ячейку корабля сверху для установки.
          </p>
        )}
        {otherActive && (
          <p className="fitting-action-reason">
            Модуль уже установлен:{" "}
            {context?.kind === "module" && context.slot === 0
              ? "Модуль 2"
              : "Модуль 1"}
            .
          </p>
        )}
        {context && !compatible && !otherActive && (
          <p className="fitting-action-reason">
            Предмет не подходит к выбранному слоту.
          </p>
        )}
        {owned && noFree && !selected && (
          <p className="fitting-action-reason">
            Все копии {grade} установлены. Сними одну с корабля или купи ещё.
          </p>
        )}
        {!owned && (
          <p className="fitting-action-reason">
            {progressionAllowed
              ? "Новые копии доступны в магазине оснащения."
              : "Во время обучения доступно только принадлежащее оснащение."}
          </p>
        )}
        {owned && progressionAllowed && tier < 2 && <p className="fitting-upgrade-preview">
          Следующая MK {"I".repeat(nextTier + 1)}: {equipmentDisplayParameters(key, nextTier, profile.hullId).map(parameter => `${parameter.label}: ${equipmentValue(parameter.value, parameter.unit)}`).join(" · ")}
        </p>}
        {owned && progressionAllowed && <details className="fitting-production">
          <summary>Разбор предмета</summary>
            <button
              type="button"
              data-equipment-action="dismantle"
              disabled={busy || free <= 0}
              onClick={() => {
                if (!busy && free > 0) void onDismantle(key, tier);
              }}
            >
              РАЗОБРАТЬ {grade} · +{item.dismantleComponents} КОМП.
            </button>
            {free <= 0 && (
              <p className="fitting-action-reason">
                Все копии {grade} установлены. Для разбора сними или замени
                предмет.
              </p>
            )}
        </details>}

      </div>
      <div className="fitting-detail-actions">
        {trainingInstall && <p className="fitting-step-hint">{trainingInstallInstruction(previousDefinition?.name ?? null)}</p>}

        {owned && (
          <button
            type="button"
            className="fitting-install"
            data-equipment-action="install"
            data-tutorial-highlight={trainingInstall || undefined}
            disabled={installDisabled}
            onClick={() => {
              if (!installDisabled) void onSelect(key, tier);
            }}
          >
            {selected
              ? "УСТАНОВЛЕНО"
              : !context
                ? "ВЫБЕРИ ЯЧЕЙКУ СВЕРХУ"
                : `${previous ? "ЗАМЕНИТЬ" : "УСТАНОВИТЬ"} · ${equipmentContextLabel(context)}`}
          </button>
        )}
        {busy && (
          <p className="fitting-action-reason" role="status">
            Подтверждаем операцию…
          </p>
        )}
        {owned && progressionAllowed && <>
          {needsUpgradeCell && <p className="fitting-action-reason">Все копии установлены. Выбери ячейку с этим предметом для улучшения.</p>}
          {upgradeShortfall && <p className="fitting-action-reason">Не хватает {upgradeShortfall} для улучшения.</p>}
          {cost ? <button type="button" className="fitting-upgrade" data-equipment-action="upgrade" disabled={upgradeDisabled}
            onClick={() => { if (!upgradeDisabled) void onUpgrade(key, tier); }}>
            УЛУЧШИТЬ ДО MK {"I".repeat(nextTier + 1)} · {cost.credits} КР + {cost.components} КОМП.
          </button> : tier === 2 ? <p className="fitting-upgrade-maximum">МАКСИМУМ · MK III</p> : <p className="fitting-action-reason" role="status">
            {upgradePricing.error ?? "Загружаем стоимость улучшения…"}
            {upgradePricing.error && <button type="button" onClick={() => void upgradePricing.reload()}>Повторить загрузку</button>}
          </p>}
        </>}

      </div>
    </article>
  );
}
